import { useState } from "react";

const SLOT_COLORS = { Breakfast: "#BA7517", Lunch: "#1D9E75", Dinner: "#534AB7" };

// Two pauses conflict only if their date ranges overlap AND share a meal slot.
// Same day + different meals (Lunch vs Dinner) is allowed.
export const datesOverlap = (aFrom, aTo, bFrom, bTo) => aFrom <= bTo && bFrom <= aTo;

export const findPauseConflict = (existing, from, to, slots, ignoreId = null) => {
  const slotSet = new Set(slots);
  return (existing || []).find(p =>
    p.id !== ignoreId &&
    datesOverlap(from, to, p.from, p.to) &&
    (p.slots || []).some(s => slotSet.has(s))
  ) || null;
};

export default function PauseModal({ subscriber, onSave, onClose, isCustomerPortal = false }) {
  const today    = new Date().toISOString().slice(0, 10);
  const tomorrow = (() => { const d = new Date(); d.setDate(d.getDate() + 1); return d.toISOString().slice(0, 10); })();

  // Customer portal: dates start from tomorrow. Admin: no restriction (can backdate).
  const minFrom  = isCustomerPortal ? tomorrow : undefined;
  const initDate = isCustomerPortal ? tomorrow : today;

  // Only allow pausing slots the subscriber actually subscribes to
  const availableSlots = subscriber.mealSlots || ["Lunch"];

  const [fromDate, setFromDate] = useState(initDate);
  const [toDate,   setToDate]   = useState(initDate);
  const [slots,    setSlots]    = useState([...availableSlots]); // default: all their slots selected
  const [error,    setError]    = useState("");

  const existing = subscriber.pausePeriods || [];

  const toggleSlot = (sl) => setSlots(prev => prev.includes(sl) ? prev.filter(s => s !== sl) : [...prev, sl]);

  const handleSave = () => {
    if (!fromDate || !toDate) { setError("Please select both dates."); return; }
    if (isCustomerPortal && fromDate < tomorrow) { setError("Pause can only start from tomorrow onwards."); return; }
    if (toDate < fromDate)    { setError("End date must be on or after start date."); return; }
    if (slots.length === 0)   { setError("Select at least one meal to pause."); return; }

    const conflict = findPauseConflict(existing, fromDate, toDate, slots);
    if (conflict) {
      const shared = (conflict.slots || []).filter(s => slots.includes(s));
      setError(
        `This overlaps an existing pause (${conflict.from} → ${conflict.to}) ` +
        `for ${shared.join(", ")}. Pick different dates or meals.`
      );
      return;
    }

    onSave({ id: Date.now(), from: fromDate, to: toDate, slots });
  };

  const removePause = (id) => onSave(null, id);

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, fontFamily: "'DM Sans','Segoe UI',sans-serif" }}>
      <div style={{ background: "#FFF", borderRadius: 16, padding: "1.75rem", width: 460, maxHeight: "88vh", overflowY: "auto" }}>

        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1.25rem" }}>
          <div>
            <h3 style={{ fontSize: 16, fontWeight: 600, margin: 0 }}>Pause deliveries</h3>
            <div style={{ fontSize: 12, color: "#78716C", marginTop: 3 }}>{subscriber.name}</div>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 18, color: "#A8A29E" }}>✕</button>
        </div>

        {/* Existing pauses — admin only; customers cannot remove pauses from this modal */}
        {!isCustomerPortal && existing.length > 0 && (
          <div style={{ marginBottom: "1.25rem" }}>
            <div style={{ fontSize: 12, fontWeight: 500, color: "#78716C", marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.04em" }}>Existing pauses</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {existing.map(p => (
                <div key={p.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", background: "#F7F6F2", borderRadius: 8, padding: "8px 12px" }}>
                  <div>
                    <div style={{ fontSize: 13, color: "#1C1917" }}>{p.from} → {p.to}</div>
                    <div style={{ display: "flex", gap: 4, marginTop: 4 }}>
                      {p.slots.map(sl => (
                        <span key={sl} style={{ fontSize: 10, fontWeight: 500, padding: "2px 7px", borderRadius: 20, background: SLOT_COLORS[sl] + "22", color: SLOT_COLORS[sl] }}>{sl}</span>
                      ))}
                    </div>
                  </div>
                  <button onClick={() => removePause(p.id)}
                    style={{ fontSize: 11, padding: "3px 10px", borderRadius: 6, border: "0.5px solid #F09595", background: "#FCEBEB", color: "#A32D2D", cursor: "pointer" }}>
                    Remove
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Add new pause */}
        <div style={{ borderTop: (!isCustomerPortal && existing.length > 0) ? "0.5px solid #F3F2EE" : "none", paddingTop: (!isCustomerPortal && existing.length > 0) ? "1.25rem" : 0 }}>
          <div style={{ fontSize: 12, fontWeight: 500, color: "#78716C", marginBottom: 12, textTransform: "uppercase", letterSpacing: "0.04em" }}>
            {isCustomerPortal ? "Select pause dates" : "Add new pause"}
          </div>

          {/* Date range */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 14 }}>
            <div>
              <label style={{ display: "block", fontSize: 12, color: "#78716C", marginBottom: 5 }}>From date</label>
              <input type="date" value={fromDate} min={minFrom} onChange={e => { setFromDate(e.target.value); setError(""); }}
                style={{ width: "100%", padding: "8px 10px", fontSize: 13, borderRadius: 8, border: "0.5px solid #E7E5E0", outline: "none", boxSizing: "border-box" }} />
            </div>
            <div>
              <label style={{ display: "block", fontSize: 12, color: "#78716C", marginBottom: 5 }}>To date</label>
              <input type="date" value={toDate} min={fromDate} onChange={e => { setToDate(e.target.value); setError(""); }}
                style={{ width: "100%", padding: "8px 10px", fontSize: 13, borderRadius: 8, border: "0.5px solid #E7E5E0", outline: "none", boxSizing: "border-box" }} />
            </div>
          </div>

          {/* Meal slots to pause — only subscriber's own slots shown */}
          <div style={{ marginBottom: 14 }}>
            <label style={{ display: "block", fontSize: 12, color: "#78716C", marginBottom: 8 }}>
              Pause which meals?
              <span style={{ color: "#A8A29E", marginLeft: 6, fontStyle: "italic" }}>({availableSlots.join(", ")})</span>
            </label>
            <div style={{ display: "flex", gap: 8 }}>
              {availableSlots.map(sl => {
                const active = slots.includes(sl);
                return (
                  <button key={sl} onClick={() => toggleSlot(sl)} style={{
                    flex: 1, padding: "8px 0", borderRadius: 8, fontSize: 13, cursor: "pointer",
                    border: `0.5px solid ${active ? SLOT_COLORS[sl] : "#E7E5E0"}`,
                    background: active ? SLOT_COLORS[sl] + "18" : "#FFF",
                    color: active ? SLOT_COLORS[sl] : "#78716C",
                    fontWeight: active ? 500 : 400, transition: "all 0.15s",
                  }}>
                    {active ? "✓ " : ""}{sl}
                  </button>
                );
              })}
            </div>
          </div>

          {error && (
            <div style={{ background: "#FCEBEB", border: "0.5px solid #F09595", borderRadius: 8, padding: "8px 12px", fontSize: 12, color: "#A32D2D", marginBottom: 12 }}>
              {error}
            </div>
          )}

          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={handleSave} style={{ flex: 1, padding: "9px", fontSize: 13, fontWeight: 500, borderRadius: 8, border: "none", background: "#1C1917", color: "#FFF", cursor: "pointer" }}>
              Save pause
            </button>
            <button onClick={onClose} style={{ padding: "9px 18px", fontSize: 13, borderRadius: 8, border: "0.5px solid #E7E5E0", background: "#FFF", cursor: "pointer" }}>
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
